<template>
  <div
    class="
      overflow-hidden rounded-bl-xl will-change-transform
      print:break-inside-avoid-page
    "
  >
    <!-- HEADER -->
    <div class="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1">
      <!-- Date -->
      <time
        class="
          relative flex w-fit items-center rounded-bl-lg bg-gray-500 py-2 pl-3
          pr-8 text-base font-semibold leading-none text-white
        "
        :datetime="data.date"
      >{{ dateFormatted }}</time>
      <!-- Publisher -->
      <p v-if="publisher" class="line-clamp-1 text-gray-500">
        {{ publisher }}
      </p>
    </div>

    <!-- CONTENT -->
    <div class="bg-gray-200" :class="{'grid grid-cols-2 max-sm:grid-cols-1': !isSidebar}">
      <!-- Image -->
      <div v-if="!isSidebar" class="relative">
        <AppImage
          v-if="data.image"
          :src="data.image"
          :aspect-ratio="3 / 2"
          class="absolute inset-0 size-full object-contain"
        />
      </div>
      <!-- Text -->
      <div class="p-5">
        <!-- Category -->
        <p v-if="data.news_category" class="mb-1 text-sm text-gray-500">
          {{ data.news_category.title }}
        </p>

        <h3
          class="
            typo-h-3 mb-3 line-clamp-3
            sm:mb-5
          "
        >
          {{ data.heading }}
        </h3>

        <div
          v-if="data.text"
          class="wysiwyg-only-lists line-clamp-5"
          v-html="data.text"
        />

        <!-- Links -->
        <div v-if="links" class="mt-6 flex flex-wrap gap-x-3 gap-y-1">
          <template v-for="link in links">
            <AppButton
              v-if="link.link"
              :key="link.id"
              :to="link.link"
              variant="primary"
              :show-external-icon="link.isExternal"
              class="hover-focus:bg-transparent"
            >
              {{ link.link_text }}
            </AppButton>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  data: {
    type: Object, required: true,
  },
})

const isSidebar = inject<boolean>('isSidebar', false)

const {
  t, locale, locales,
} = useI18n()

const dateFormatted = computed(() => {
  if (!props.data.date) return

  const dateObj = new Date(props.data.date)

  const options = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }

  // Find the locale in the list of available locales by code
  const localeObj = locales.value.find(site => site.code === locale.value)

  return dateObj.toLocaleDateString(localeObj?.language || 'de-CH', options)
})

// Merge external link with other links
const links = computed(() => {
  /*
  * If the article has a external link, add it to the list of links.
  * Else add the articles url to the list of links
  */

  const link = props.data.link_external || props.data.permalink

  const linkObject = {
    link,
    link_text: t('more'),
    isExternal: !!props.data.link_external,
  }

  return [
    linkObject,
    ...(props.data.links && props.data.links.length ? props.data.links : []),
  ]
})

const publisher = computed(() => {
  if (props.data.publisher) {
    return props.data.publisher.title
  }

  return props.data.site_attributes.find(attr => attr.key === 'title')?.value
})
</script>
